






import config from '@/config'
import firebase from 'firebase/compat/app'
import { Location } from '@/models'
import { defineComponent, onMounted, onUnmounted, ref } from '@vue/composition-api'
import { useEmitter } from '../../composition/useEmitter'
import { ProductRebillingService } from '../../../services'
import { conversationRouteNames, contactsRouteNames } from "@/routes/constants/location"

export default defineComponent({
  setup(_, ctx) {
    const maxUploadSize = ref({})
    /* we are taking default values as 4GB for videos and 100MB for other documents, Each document type size limit can be changed using this object
    Example: maxUploadSize.value = {
      video: 1000000000, <-- In bytes
      audio: 100000000,
      document: 100000000,
      image: 100000000
    }
    */
    const emitter = useEmitter()
    const route = ctx.root.$route
    const router = ctx.root.$router
    const store = ctx.root.$store
    const routerVersion = ref('v1')
    const { id: userId, email, platformLanguage } = store.state.user.user
    const { locations } = store.state.locations
    const locationData = locations.find(
      (x: Location) => x.id === route.params.location_id
    )

    const currentLocation = new Location(
      store.getters['locations/getCurrentLocation']
    )
    const locationSpecificLanguage = currentLocation?._data?.locale_settings?.platform

    const getUserPermission = () => {
      const permission = {
        contentAIEnabled: true,
      }
      if (store.state.user?.user?.permissions) {
        const permissions = store.state.user?.user?.permissions
        permission.contentAIEnabled =
          permissions.content_ai_enabled === false ? false : true
      }
      return permission
    }
    
    const isContentAiRebillingEnabled = async (companyId: string, locationId: string) => {
      const data = await ProductRebillingService.getByProductAndLocation(
        locationId,
        companyId,
        'contentAI'
      )
      if(data?.config?.optIn) return data?.config?.optIn
      return false
    }

    function getToken() {
      // @ts-ignore
      return firebase.auth().currentUser.getIdToken()
    }

    onMounted(async () => {
      // Set up event listeners for the new media library integration
      emitter.on('mediaSelected', (file) => {
        if (file) {
          ctx.emit('mediaSelected', file)
          // Close the media modal when a file is selected
          emitter.emit('closeMediaModal')
          // Also trigger exitML to ensure parent components know to close their UI
          ctx.emit('exitML', {})
        }
      })
      
      emitter.on('media-library-closed', () => {
        // Close the media modal when explicitly closed
        emitter.emit('closeMediaModal')
        if([contactsRouteNames.contactDetail, conversationRouteNames.conversationsIdV2].includes(route.name as string)) {
          ctx.emit('exitML', {})
        } else {
          const routeName = 'media-settings-v2'
          router.push({ name: routeName })
        }
      })

      // Set up the media library opener based on component mount
      const features = {
        contentAI: await isContentAiRebillingEnabled(
          locationData.company_id,
          locationData.id
        ),
      }
      // Configure settings for the media library
      const settings = {
        companyId: locationData.company_id,
        altId: locationData.id,
        altType: 'location',
        allowedExtensions: ['documents', 'image', 'video', 'audio'],
        maxUploadSize: maxUploadSize.value,
        selfHosted: false,
        contentAI: features.contentAI,
        allowMultipleFileInsertion: false,
        // Additional properties from old implementation
        userId,
        email,
        apiKey: locationData.api_key,
        tokenId: await getToken(),
        userPermission: getUserPermission(),
        localeLanguage: platformLanguage ?? locationSpecificLanguage ?? 'en_US'
      }

      // Open the media library when component mounts
      emitter.emit('media-library-open', settings)

      // Setup handler for content-ai-generated-image
      emitter.on('content-ai-generated-image', (id: string) => {
        if (id) {
          emitter.emit('selectedImageAIVariation', id)
        }
      })
    })

    onUnmounted(() => {
      // Remove event listeners
      emitter.off('mediaSelected')
      emitter.off('media-library-closed')
      emitter.off('content-ai-generated-image')
    })

    return {
      routerVersion
    }
  },
  beforeRouteEnter(to, from, next) {
    next((vm: any) => {
      vm.routerVersion = from?.meta?.router_version || 'v1'
      next()
    })
  },
})
